import { css } from '@repo/pandacss/styled-system/css';
import { type StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { ProductCard as ProductCardType } from '@repo/supergraph/roundup/base/types';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import React from 'react';
import { filterEntitiesByState } from '../../utils/geo-data';
import ProductCardWrapper from './ProductCardsWrapper';
import { ProductCardCategorySectionsSkeleton } from './Skeleton';

const getBadgeText = (sectionEyebrowText?: string, sectionTitle?: string): string | undefined => {
  if (sectionEyebrowText && sectionTitle) {
    return `${sectionEyebrowText} ${sectionTitle}`;
  }
};

interface Props {
  deviceType?: MobileOrDesktop;
  title?: string;
  description?: string;
  eyebrowText?: string;
  productCards: ProductCardType[];
  productPositionBase: number;
  isOurTakeExpanded: boolean;
  pageType?: 'roundup' | 'paid-roundup';
  regionCode?: StateAbbreviation;
  shouldApplyStylingUpdates?: boolean;
  showSkeleton?: boolean;
  slug?: string;
}

export default function CategorySection({
  deviceType,
  title,
  description,
  eyebrowText,
  productCards,
  productPositionBase,
  isOurTakeExpanded,
  pageType,
  regionCode,
  shouldApplyStylingUpdates = false,
  showSkeleton = false,
  slug,
}: Props): React.JSX.Element | null {
  const geoTargetedCards = regionCode
    ? filterEntitiesByState<ProductCardType>(productCards, regionCode)
    : productCards;

  if (!geoTargetedCards.length) {
    return null;
  }

  if (showSkeleton) {
    return (
      <ProductCardCategorySectionsSkeleton
        showTitleSkeleton={!!title || !!eyebrowText}
        cardsAmount={geoTargetedCards.length}
        isOurTakeExpanded={isOurTakeExpanded}
      />
    );
  }

  const cardBadgeText = getBadgeText(eyebrowText, title);

  return (
    <li
      className={css({
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '0',
        paddingRight: '0',
        paddingBottom: '0',
        paddingLeft: '0',
        maxWidth: { base: 'full', md: '[82%]' },
        marginInlineStart: 'auto',
        marginInlineEnd: 'auto',
        marginBottom: '16',
      })}
      data-testid="product-card-section"
    >
      {(title || eyebrowText) && (
        <div className={css({ marginBottom: '4' })}>
          {eyebrowText && (
            <p className={css({ textStyle: 'textBold', paddingBottom: '2' })}>{eyebrowText}</p>
          )}
          {title && <p className={css({ textStyle: 'textLargeBold' })}>{title}</p>}
          {description && <p className={css({ marginTop: '1' })}>{description}</p>}
        </div>
      )}
      <ul
        className={css({
          paddingTop: '0',
          paddingRight: '0',
          paddingBottom: '0',
          paddingLeft: '0',
        })}
      >
        {geoTargetedCards.map((card, cardIndex) => (
          <ProductCardWrapper
            card={card}
            cardBadgeText={cardIndex === 0 ? cardBadgeText : undefined}
            cardIndex={cardIndex}
            deviceType={deviceType}
            isOurTakeExpanded={isOurTakeExpanded}
            key={card.marketplaceEntity.id}
            pageType={pageType}
            productPositionBase={productPositionBase}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            slug={slug}
          />
        ))}
      </ul>
    </li>
  );
}
